import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { AboutPage, AvailabilityPage, DocPage, EarlyAccessPage, NotFoundPage, TrustPage as TrustView, docs } from "./site/MorePages";
import Lenis from "lenis";
import MotionLabGallery from "./motion/MotionLabGallery";
import { DisciplineView, HomeSections, ProductView, SiteFooter, disciplinePages } from "./site/Pages";

function BrandWord({ className = "" }: { className?: string }) {
  return <span className={className}><span aria-hidden="true">h<span className="brand-signature-i">ı</span>ndset</span><span className="sr-only">Hindset</span></span>;
}

function Brand() {
  return <a className="brand" href="/" aria-label="Hindset home"><picture className="brand-logo-source"><source srcSet="/brand/hindset-mark.svg" type="image/svg+xml" /><img className="brand-logo" src="/brand/hindset-mark-4096.png" alt="" width={32} height={32} /></picture><BrandWord /></a>;
}

const primaryNav = [
  ["Product", "/product"],
  ["Gaming", "/gaming"],
  ["Editing", "/editing"],
  ["Design", "/design"],
  ["Development", "/development"],
  ["Availability", "/download"],
] as const;

function Header() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, []);
  return <header className="site-header"><Brand /><nav id="primary-navigation" className={open ? "open" : ""} aria-label="Main navigation">{primaryNav.map(([label, href]) => <a key={href} className={window.location.pathname === href ? "active" : ""} aria-current={window.location.pathname === href ? "page" : undefined} href={href} onClick={() => setOpen(false)}>{label}</a>)}<a className="mobile-account-link" href="/early-access" onClick={() => setOpen(false)}>Early access</a></nav><div className="header-actions"><a className="header-login" href="/trust">Trust</a><a className="header-download" href="/early-access">Get early access</a></div><button className="nav-toggle" type="button" aria-expanded={open} aria-controls="primary-navigation" onClick={() => setOpen(value => !value)}><span /><span /><span className="sr-only">Menu</span></button></header>;
}

function Footer() {
  return <SiteFooter brand={<Brand />} />;
}

export default function App() {
  const [location, setLocation] = useState(() => `${window.location.pathname}${window.location.search}`);

  useEffect(() => {
    const updateLocation = () => setLocation(`${window.location.pathname}${window.location.search}`);
    const navigateWithinSite = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest<HTMLAnchorElement>("a[href]");
      if (!anchor || anchor.target && anchor.target !== "_self" || anchor.hasAttribute("download")) return;
      const destination = new URL(anchor.href, window.location.href);
      if (destination.origin !== window.location.origin || !["http:", "https:"].includes(destination.protocol)) return;
      if (destination.pathname === window.location.pathname && destination.search === window.location.search && destination.hash) return;

      event.preventDefault();
      window.history.pushState({}, "", `${destination.pathname}${destination.search}${destination.hash}`);
      updateLocation();
    };

    document.addEventListener("click", navigateWithinSite);
    window.addEventListener("popstate", updateLocation);
    return () => {
      document.removeEventListener("click", navigateWithinSite);
      window.removeEventListener("popstate", updateLocation);
    };
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      let anchor: HTMLElement | null = null;
      try { anchor = document.getElementById(decodeURIComponent(window.location.hash.slice(1))); } catch { /* Malformed fragments must not break navigation. */ }
      if (anchor) anchor.scrollIntoView();
      else window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      const main = document.querySelector<HTMLElement>("main");
      main?.setAttribute("tabindex", "-1");
      main?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location]);

  const pathname = new URL(location, window.location.origin).pathname.replace(/\/+$/, "") || "/";
  const discipline = disciplinePages[pathname];
  if (discipline) return <PageFrame><DisciplineView page={discipline} /></PageFrame>;
  if (pathname === "/product") return <ProductPage />;
  if (pathname === "/motion-lab") return <MotionLabPage />;
  if (pathname === "/trust") return <PageFrame><TrustView /></PageFrame>;
  if (pathname === "/get-started" || pathname === "/download") return <PageFrame><AvailabilityPage /></PageFrame>;
  if (pathname === "/account" || pathname === "/early-access" || /^\/r\/[a-z0-9]{4,12}$/i.test(pathname)) return <PageFrame><EarlyAccessPage /></PageFrame>;
  if (pathname === "/about") return <PageFrame><AboutPage /></PageFrame>;
  const doc = docs[pathname];
  if (doc) return <PageFrame><DocPage doc={doc} /></PageFrame>;
  if (pathname === "/") return <Home />;
  return <PageFrame><NotFoundPage /></PageFrame>;
}

function PageFrame({ children }: { children: ReactNode }) {
  return <><a className="skip" href="#main">Skip to content</a><div className="plain-header"><Header /></div><main id="main">{children}</main><Footer /></>;
}

function MotionLabPage() {
  useEffect(() => {
    document.title = "Motion lab — Hindset";
    return () => { document.title = "Hindset — Learn what can’t be taught"; };
  }, []);
  return <PageFrame><MotionLabGallery /></PageFrame>;
}

function ProductPage() {
  return <PageFrame><ProductView /></PageFrame>;
}

// The animated film is about 1 MB. Paint its 30 KB still first and fade the motion in once the page has loaded,
// so the first meaningful paint isn't waiting on the animation.
function HeroFilm() {
  const [src, setSrc] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const start = () => window.setTimeout(() => setSrc("/media/ripple-delete-right.webp"), 150);
    if (document.readyState === "complete") start(); else window.addEventListener("load", start, { once: true });
    return () => window.removeEventListener("load", start);
  }, []);
  return src ? <img className={`hero-film hero-film-motion ${ready ? "ready" : ""}`} src={src} alt="" width={600} height={338} decoding="async" onLoad={() => setReady(true)} /> : null;
}

function Home() {
  const heroScene = useRef<HTMLDivElement>(null);
  const lenis = useRef<Lenis | null>(null);

  useEffect(() => {
    if (!window.matchMedia('(pointer: fine)').matches) return;

    const smoothScroll = new Lenis({
      anchors: true,
      autoRaf: true,
      lerp: .12,
      respectReducedMotion: true,
      smoothWheel: true,
      stopInertiaOnNavigate: true,
      syncTouch: false,
      wheelMultiplier: 1,
    });
    lenis.current = smoothScroll;

    return () => {
      smoothScroll.destroy();
      lenis.current = null;
    };
  }, []);

  useEffect(() => {
    const scene = heroScene.current;
    if (!scene) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const narrowLayout = window.matchMedia('(max-width: 760px)');
    let animationFrame = 0;

    const setProgress = (progress: number) => {
      const dock = scene.querySelector<HTMLElement>('.hero-media-dock');
      const header = scene.querySelector<HTMLElement>('header');
      const media = scene.querySelector<HTMLElement>('.hero-media');
      const scaledEdge = (media?.offsetHeight ?? 580) * .1;
      const headerOverlap = 14;
      const travel = Math.max(0, (dock?.offsetTop ?? 680) - (header?.offsetHeight ?? 100) - scaledEdge + headerOverlap);
      const remaining = 1 - progress;
      const edgeProgress = Math.max(0, (progress - .18) / .82);
      const restingLift = 20;
      scene.style.setProperty('--hero-media-y', `${-(travel + restingLift) * remaining}px`);
      scene.style.setProperty('--hero-media-scale', `${1 + .2 * remaining}`);
      scene.style.setProperty('--hero-media-blur', `${4.5 * remaining}px`);
      scene.style.setProperty('--hero-media-brightness', `${.74 + .26 * progress}`);
      scene.style.setProperty('--hero-media-copy', `${Math.max(0, (progress - .3) / .7)}`);
      scene.style.setProperty('--hero-media-radius', `${2 + 6 * progress}px`);
      scene.style.setProperty('--hero-media-mask-solid', `${20 + 74 * edgeProgress}%`);
      scene.style.setProperty('--hero-media-mask-edge', `${92 + 8 * edgeProgress}%`);
      scene.style.setProperty('--hero-overlay-opacity', `${.62 * remaining}`);
    };

    const update = () => {
      animationFrame = 0;
      if (reducedMotion.matches || narrowLayout.matches) {
        setProgress(1);
        return;
      }
      const range = Math.min(520, window.innerHeight * .58);
      const progress = Math.min(1, Math.max(0, window.scrollY / range));
      setProgress(progress);
    };
    const scheduleUpdate = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(update);
    };

    update();
    const removeSmoothScrollListener = lenis.current?.on('scroll', scheduleUpdate);
    if (!removeSmoothScrollListener) window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    reducedMotion.addEventListener('change', scheduleUpdate);
    narrowLayout.addEventListener('change', scheduleUpdate);
    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      removeSmoothScrollListener?.();
      if (!removeSmoothScrollListener) window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      reducedMotion.removeEventListener('change', scheduleUpdate);
      narrowLayout.removeEventListener('change', scheduleUpdate);
    };
  }, []);

  return <>
    <a className="skip" href="#main">Skip to content</a>
    <div className="home-header"><Header /></div>
    <main id="main">
      <div className="hero-landscape" id="top" ref={heroScene}>
        <section className="hero">
          <h1><span className="hero-accent">Learn</span> <span className="hero-light">what can<span className="hero-diamond-apostrophe" aria-hidden="true" /><span className="sr-only">’</span>t</span>{" "}<br /><em>be taught.</em></h1>
          <div className="hero-explainer"><p><BrandWord className="hero-wordmark" /> watches your sessions and shows you<br className="hero-break" /> the moments that cost you, whatever your <a className="craft-link" href="#possibilities">craft</a>.</p></div>
          <div className="hero-action"><a className="download" href="/early-access"><span>Get early access</span><span className="download-arrow" aria-hidden="true">→</span></a><a className="platforms-button" href="#app"><span>See the app</span></a></div>
        </section>
        <div className="hero-media-dock"><div className="film-stage media-placeholder hero-media" aria-hidden="true"><img className="hero-film hero-film-still" src="/media/ripple-delete-right-poster.webp" alt="" width={600} height={338} fetchPriority="high" /><HeroFilm /></div></div>
      </div>
      <HomeSections />
    </main>
    <Footer />
  </>;
}
